import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, CalendarCheck, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import BloqLogo from "@/components/BloqLogo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatInTz, userBrowserTimezone, COMMON_TIMEZONES } from "@/lib/time";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Public booking page (no auth required).
 * Shows host, availability, lets visitor pick a time and submit.
 */
export default function PublicBooking() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [tz, setTz] = useState(userBrowserTimezone());
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/bookings/public/${slug}`).then((r) => setLink(r.data)).catch(() => setError("This booking link is no longer active."));
  }, [slug]);

  const days = useMemo(() => {
    if (!link) return [];
    const out = [];
    const weekdays = link.availability?.weekdays || [];
    for (let i = 0; i < 14; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      if (weekdays.includes(d.getDay())) out.push(d);
    }
    return out;
  }, [link]);

  const slots = useMemo(() => {
    if (!selectedDay || !link) return [];
    const start = link.availability?.start_hour ?? 9;
    const end = link.availability?.end_hour ?? 17;
    const dur = link.duration_minutes;
    const out = [];
    const step = dur;
    for (let mins = start * 60; mins + dur <= end * 60; mins += step) {
      const d = new Date(selectedDay);
      d.setHours(0, 0, 0, 0);
      d.setMinutes(mins);
      out.push(d);
    }
    return out;
  }, [selectedDay, link]);

  const submit = async () => {
    if (!name.trim() || !email.trim() || !selectedSlot) {
      toast.error("Please fill in name, email, and pick a time");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/public/${slug}/submit`, {
        name: name.trim(),
        email: email.trim(),
        notes: notes.trim(),
        selected_time: selectedSlot.toISOString(),
        timezone: tz,
      });
      navigate(`/booking-confirmed`, { state: { booking: data, link, tz, slot: selectedSlot.toISOString() } });
    } catch (e) {
      toast.error("Could not complete booking");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#fbfcfc]">
        <div className="bloq-card p-8 max-w-md text-center">
          <BloqLogo size={26} />
          <p className="bloq-headline text-2xl mt-4">Booking not available</p>
          <p className="text-sm text-[#4a4a52] mt-2">{error}</p>
        </div>
      </div>
    );
  }
  if (!link) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#fbfcfc]">
        <div className="bloq-spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fbfcfc]" data-testid="public-booking-page">
      <div className="bloq-container pt-6">
        <BloqLogo size={24} />
      </div>
      <div className="bloq-container py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-1">
          <Avatar className="w-16 h-16 mb-4">
            <AvatarImage src={link.host_avatar || ""} />
            <AvatarFallback className="bg-[#005e7a] text-white">{(link.host_name || "B").slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <p className="text-sm text-[#82828c]">{link.host_name} · {link.workspace_name}</p>
          <h1 className="bloq-headline text-3xl mt-1">{link.title}</h1>
          <p className="inline-flex items-center gap-2 text-sm text-[#4a4a52] mt-3"><Clock className="w-4 h-4" /> {link.duration_minutes} minutes</p>
          {link.description && <p className="text-sm text-[#4a4a52] mt-4 leading-relaxed">{link.description}</p>}
          <div className="mt-6">
            <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Your timezone</label>
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger className="mt-1" data-testid="visitor-tz-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-60">
                {COMMON_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="lg:col-span-2 bloq-card p-6">
          <p className="bloq-headline text-xl mb-4">Pick a day</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 mb-6">
            {days.map((d) => {
              const isSel = selectedDay && d.toDateString() === selectedDay.toDateString();
              return (
                <button
                  key={d.toISOString()}
                  onClick={() => { setSelectedDay(d); setSelectedSlot(null); }}
                  className="p-3 rounded-lg text-left transition-colors"
                  style={{ background: isSel ? "#005e7a" : "#f5efe6", color: isSel ? "#fff" : "#1a1a1f" }}
                  data-testid={`booking-day-${d.toISOString().slice(0,10)}`}
                >
                  <p className="text-[10px] uppercase tracking-widest opacity-70">{d.toLocaleDateString(undefined, { weekday: "short" })}</p>
                  <p className="text-xl bloq-display">{d.getDate()}</p>
                  <p className="text-[10px] opacity-70">{d.toLocaleDateString(undefined, { month: "short" })}</p>
                </button>
              );
            })}
          </div>
          {selectedDay && (
            <>
              <p className="bloq-headline text-lg mb-3">Pick a time</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6">
                {slots.map((s) => {
                  const isSel = selectedSlot && s.getTime() === selectedSlot.getTime();
                  return (
                    <button
                      key={s.toISOString()}
                      onClick={() => setSelectedSlot(s)}
                      className="px-3 py-2 rounded-md text-sm transition-colors"
                      style={{ background: isSel ? "#005e7a" : "#fff", color: isSel ? "#fff" : "#1a1a1f", boxShadow: "0 2px 6px rgba(0,0,0,0.04)" }}
                      data-testid={`booking-slot-${s.toISOString()}`}
                    >
                      {formatInTz(s.toISOString(), tz, { dateStyle: undefined, timeStyle: undefined, hour: "numeric", minute: "2-digit" }).replace(/.*?, /, "")}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {selectedSlot && (
            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              <Field label="Your name">
                <input className="bloq-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="booking-name-input" />
              </Field>
              <Field label="Email">
                <input type="email" className="bloq-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="booking-email-input" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Anything we should know? (optional)">
                  <textarea rows={3} className="bloq-input" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="booking-notes-input" />
                </Field>
              </div>
              <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-3 mt-2">
                <p className="text-sm text-[#4a4a52]">
                  Confirming for <strong>{formatInTz(selectedSlot.toISOString(), tz)}</strong>
                </p>
                <button onClick={submit} className="bloq-btn-primary inline-flex items-center gap-2" disabled={busy} data-testid="booking-submit-button">
                  {busy ? "Booking…" : (<>Confirm booking <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
